import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, doc, setDoc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import './UserManagement.css';
import { useAuth } from './App'; // Import the hook from App.js

function UserManagement() {
    const { user: currentUser, loading: authLoading } = useAuth(); // Get the currently logged-in user and the auth loading state
    const [users, setUsers] = useState([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('Researcher');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        setIsLoading(true);
        const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
            const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUsers(usersData);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching users: ", err);
            setError("Could not load user data. Check security rules.");
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const showTemporaryMessage = (setter, message) => {
        setter(message);
        setTimeout(() => setter(''), 5000);
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        if (!inviteEmail) {
            setError("Please enter an email to invite.");
            return;
        }

        try {
            const auth = getAuth();
            const tempPassword = Math.random().toString(36).slice(-8); 
            
            const userCredential = await createUserWithEmailAndPassword(auth, inviteEmail, tempPassword);
            const newUser = userCredential.user;

            await setDoc(doc(db, "users", newUser.uid), {
                email: newUser.email,
                role: inviteRole
            });
            
            await sendPasswordResetEmail(auth, newUser.email);
            showTemporaryMessage(setSuccessMessage, `User ${newUser.email} created. An invite with a password reset link has been sent.`);
            setInviteEmail('');

        } catch (err) {
            console.error("Error inviting user:", err);
            if (err.code === 'auth/email-already-in-use') {
                showTemporaryMessage(setError, 'This email address is already in use.');
            } else {
                showTemporaryMessage(setError, `Failed to invite user. (${err.message})`);
            }
        }
    };

    const handleSendPasswordReset = async (email) => {
        setError('');
        setSuccessMessage('');
        const auth = getAuth();
        try {
            await sendPasswordResetEmail(auth, email);
            showTemporaryMessage(setSuccessMessage, `Password reset email sent to ${email}.`);
        } catch (err) {
            console.error("Error sending password reset email:", err);
            showTemporaryMessage(setError, "Failed to send password reset email.");
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        // Prevent a Super Admin from demoting their own account
        if (currentUser.uid === userId && newRole !== 'Super Admin') {
            alert("Error: You cannot demote your own Super Admin account.");
            // Re-fetch users to revert the visual change in the dropdown
            const userDoc = await getDoc(doc(db, "users", userId));
            if(userDoc.exists()) {
                 setUsers(users.map(u => u.id === userId ? { ...u, role: userDoc.data().role } : u));
            }
            return;
        }
        const userRef = doc(db, "users", userId);
        try {
            await updateDoc(userRef, { role: newRole });
             setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
        } catch (err) {
            console.error("Error updating role:", err);
        }
    };

    const handleRemoveUser = async (userId, userEmail) => {
        // Prevent a Super Admin from deleting their own account
        if (currentUser.uid === userId) {
            alert("Error: You cannot remove your own account.");
            return;
        }
        if (window.confirm(`Are you sure you want to remove user ${userEmail}? This will remove their role and dashboard access.`)) {
            try {
                // This only deletes the Firestore document, not the Auth user.
                // For a full deletion, you would need to use the Firebase Admin SDK on a backend.
                await deleteDoc(doc(db, "users", userId));
                showTemporaryMessage(setSuccessMessage, `User role for ${userEmail} removed. The user can still sign in but will have the default role.`);
            } catch (err) {
                console.error("Error removing user:", err);
            }
        }
    };

    return (
        <div className="user-management-container">
            <h1>User Management</h1>
            <p>Invite new users and manage their roles. Invited users will receive an email to set their own password.</p>
            {error && <p className="error-message">{error}</p>}
            {successMessage && <p className="success-message">{successMessage}</p>}

            <div className="card user-list-section">
                <h3>Invite & Manage Users</h3>
                <form onSubmit={handleInvite} className="invite-form">
                    <input 
                        type="email" 
                        placeholder="user@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        required 
                    />
                    <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                        <option value="Researcher">Researcher</option>
                        <option value="Admin">Admin</option>
                        <option value="Super Admin">Super Admin</option>
                    </select>
                    <button type="submit">Create & Invite User</button>
                </form>

                <hr className="divider" />
                
                {isLoading || authLoading ? <p>Loading users...</p> : (
                    <div className="table-wrapper">
                        <table className="user-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.email}</td>
                                        <td>
                                            <select 
                                                value={user.role} 
                                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                disabled={user.id === currentUser?.uid}
                                            >
                                                <option value="Researcher">Researcher</option>
                                                <option value="Admin">Admin</option>
                                                <option value="Super Admin">Super Admin</option>
                                            </select>
                                        </td>
                                        <td className="action-buttons">
                                            <button className="btn-reset" onClick={() => handleSendPasswordReset(user.email)}>
                                                Send Password Reset
                                            </button>
                                            {user.id !== currentUser?.uid && (
                                                <button 
                                                    className="btn-delete" 
                                                    onClick={() => handleRemoveUser(user.id, user.email)}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default UserManagement;
